import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  CacheKeysPreventaEnum,
  InstallScheduleData,
  useGetFlotaPlanificadorTimeMapsMutation,
  useUnblockManyHours,
} from '@/shared/actions/erp';
import {
  getCacheRedis,
  useSetCacheRedis,
} from '@/shared/actions/erp/global/cache-redis.actions';
import {
  AlertSeverityEnum,
  IDataPreventa,
} from '@/shared/interfaces';
import { useAuthStore } from '@/store/auth';
import { useGenericCountdownStore } from '@/store/erp';
import { useInstallScheduleStore } from '@/store/erp/planeacion';
import { useUiStore } from '@/store/ui';
import { TimeMapPlanificador } from '@/shared/interfaces/erp/mantenimiento-operaciones';

type UseInstallScheduleProps = {
  form: UseFormReturn<any>;
  preventa: IDataPreventa;
  cackeKey?: CacheKeysPreventaEnum;
};

export const useInstallSchedule = ({
  form,
  preventa,
  cackeKey = CacheKeysPreventaEnum.HORARIO_INSTALACION_PREAGENDA_BASE,
}: UseInstallScheduleProps) => {
  ///* local state ============================
  const [isMounted, setIsMounted] = useState(false);

  ///* global state ============================
  const setIsFormBlocked = useInstallScheduleStore(s => s.setIsFormBlocked);
  const setCacheHorarioData = useInstallScheduleStore(
    s => s.setCacheHorarioData
  );
  const setHasStartedTimer = useInstallScheduleStore(s => s.setHasStartedTimer);
  const startTimer = useGenericCountdownStore(s => s.start);
  const setInstalationDateHour = useInstallScheduleStore(
    s => s.setDateTimeInstallation
  );
  const setIsGLobalLoading = useUiStore(s => s.setIsGlobalLoading);
  const setSelectedHour = useInstallScheduleStore(s => s.setSelectedHour);
  const setAllTimeMaps = useInstallScheduleStore(s => s.setAllTimeMaps);
  const setLimitDates = useInstallScheduleStore(s => s.setLimitDates);
  const setTimeMapPreagenda = useInstallScheduleStore(s => s.setTimeMap);
  const userId = useAuthStore(s => s.user?.id);
  const setAlert = useUiStore(s => s.setAlert);

  //
  const allTimeMaps = useInstallScheduleStore(s => s.allTimeMaps);
  const setSelectedDate = useInstallScheduleStore(s => s.setSelectedDate);

  const setIsVisibleCalendario = useInstallScheduleStore(
    s => s.setIsVisibleCalendario
  );
  const setThereIsCacheData = useInstallScheduleStore(
    s => s.setThereIsCacheData
  );

  // form -----------------------------
  const watchedFlota = form.watch('flota');
  const watchedFechaInstalacion = form.watch('fecha_instalacion');

  // handlers -----------------------------
  const onSuccessGetTimeMaps = (data: TimeMapPlanificador[]) => {
    const datesArr = data.map(item => item?.fecha);
    const limitDates = [
      dayjs(datesArr[0]).format('YYYY-MM-DD'),
      dayjs(datesArr[datesArr.length - 1]).format('YYYY-MM-DD'),
    ];

    setLimitDates(limitDates);

    //
    setAllTimeMaps(data);
  };
  const onErrorGetTimeMaps = () => {
    setTimeMapPreagenda(null);
    form.setValue('fecha_instalacion', null);
  };

  ///* mutations ============================
  const getTimeMapsMutation = useGetFlotaPlanificadorTimeMapsMutation({
    customSuccessCallback: (resData: TimeMapPlanificador[]) =>
      onSuccessGetTimeMaps(resData),
    customErrorCallback: onErrorGetTimeMaps,
    //customSuccessMessage: 'Horarios obtenidos correctamente xD',
  });
  const setCacheClear = useSetCacheRedis<InstallScheduleData>({
    customSuccessMessage: 'Tiempo agotado, seleccione nuevamente el horario',
    customMessageSuccessSeverityAlert: AlertSeverityEnum.info,
  });
  const unBlockHours = useUnblockManyHours({
    enableRedirect: false,
    enableAlert: false,
  });

  ///* handlers ============================
  const getTimeMapsByFlota = async (flotaCode: string) => {
    await getTimeMapsMutation.mutateAsync({
      flotaCode: flotaCode!,
    });
  };

  const onChangeFechaInstalacion = async (fecha: string) => {
    const selectedTimeMap = allTimeMaps?.find(item => item.fecha === fecha);
    if (!selectedTimeMap) return setTimeMapPreagenda(null);

    setSelectedDate(fecha);

    // filter previous hours -------
    selectedTimeMap.horas = selectedTimeMap.horas?.filter(horaTimeMap => {
      // const hasPreventaId = !!horaTimeMap?.id_preventa;
      // if (hasPreventaId) return false;

      // si el timeMap tiene  `fecha_hora_limite`  y es mayor a la fecha actual, no pasa
      const hasFechaHoraLimite = !!horaTimeMap?.fecha_hora_limite;
      if (hasFechaHoraLimite) {
        const fechaHoraLimite = dayjs(horaTimeMap?.fecha_hora_limite);
        const currentDate = dayjs();
        if (fechaHoraLimite.isAfter(currentDate)) return false;
      }

      return true;
    });
    if (!selectedTimeMap.horas?.length) {
      setAlert(
        true,
        'No hay horarios disponibles para la fecha seleccionada',
        AlertSeverityEnum.info
      );
      setTimeMapPreagenda(null);
      return;
    }

    setTimeMapPreagenda([selectedTimeMap]);
    setSelectedHour(null);
  };

  ///* effects ============================
  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, []);

  // check cache -------------
  useEffect(() => {
    if (!isMounted) return;
    (async () => {
      setIsGLobalLoading(true);
      const res = await getCacheRedis<InstallScheduleData>({
        key: `${cackeKey}_${preventa?.id_preventa}`,
      });
      const data = res?.data;
      setIsGLobalLoading(false);

      // not found also return 200 ----------------
      if (!data || data?.preventaId != preventa?.id_preventa) return;

      setIsFormBlocked(true);
      setCacheHorarioData(data);
      setSelectedHour(data?.selectedHour?.toString() || null);
      setHasStartedTimer(true);
      setIsVisibleCalendario(data?.isVisibleCalendario || false);
      setThereIsCacheData(true);

      const fechaHoraInstalacion = dayjs(data?.selectedDate)
        .hour(+(data?.selectedHour || 0))
        .format();
      setInstalationDateHour(fechaHoraInstalacion);

      // set flota
      form.setValue('flota', data?.flotaCode);
      form.setValue('fecha_instalacion', data?.selectedDate);
      form.setValue(
        'hora_instalacion',

        dayjs()
          .hour(+(data?.selectedHour || 0))
          .minute(0)
          .format('HH:mm')
      );

      const resPlanificador = await getTimeMapsMutation.mutateAsync({
        flotaCode: data?.flotaCode,
      });
      const dataPlanificador: TimeMapPlanificador[] =
        resPlanificador?.data || ([] as any);
      const selectedTimeMap = dataPlanificador.find(
        horario => horario?.fecha === data?.selectedDate
      );
      if (!selectedTimeMap) return setTimeMapPreagenda(null);

      // filter previous hours -------
      selectedTimeMap.horas = selectedTimeMap.horas?.filter(horaTimeMap => {
        const hourTimeMap = +(horaTimeMap?.hora || 0);

        const isSelectedHour = hourTimeMap === data?.selectedHour;
        const isSameUser = userId === data?.userId;

        // Si es la hora seleccionada, sólo pasa si es el mismo usuario
        if (isSelectedHour) return isSameUser;

        // // Si tiene preventaId, no pasa
        // const hasPreventaId = !!horaTimeMap?.id_preventa;
        // if (hasPreventaId) return false;

        // // si el timeMap tiene  `fecha_hora_limite`  y es mayor a la fecha actual, no pasa
        const hasFechaHoraLimite = !!horaTimeMap?.fecha_hora_limite;
        if (hasFechaHoraLimite) {
          const fechaHoraLimite = dayjs(horaTimeMap?.fecha_hora_limite);
          const currentDate = dayjs();
          if (fechaHoraLimite.isAfter(currentDate)) return false;
        }

        return true;
      });

      setTimeMapPreagenda([selectedTimeMap]);

      // start timer -------
      const time = dayjs(data?.limitDate).diff(dayjs(), 'second');

      startTimer(time, async () => {
        useInstallScheduleStore.getState().clearAll();
        useInstallScheduleStore.getState().clearFinal();
        await unBlockHours.mutateAsync({
          fecha: data?.selectedDate,
          horas: [+(data?.selectedHour || 0)],
          flota: data?.flotaCode,
        });
        await setCacheClear.mutateAsync({
          key: `${cackeKey}_${preventa?.id_preventa}`,
          value: null,
        });

        form.setValue('flota', '');
        form.setValue('fecha_hora_instalacion', null);
        form.setValue('fecha_instalacion', null);
      });

      setSelectedHour(data?.selectedHour?.toString() || null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form,
    isMounted,
    preventa?.id_preventa,
    setCacheHorarioData,
    setIsFormBlocked,
    setTimeMapPreagenda,
  ]);

  // already has flota selected -------------
  useEffect(() => {
    if (!watchedFlota) return;

    (async () => {
      await getTimeMapsByFlota(watchedFlota);
      onChangeFechaInstalacion(watchedFechaInstalacion);
    })();
  }, [watchedFlota]);

  ///* return ============================
  return {
    getTimeMapsByFlota,

    onSuccessGetTimeMaps,
    onErrorGetTimeMaps,

    onChangeFechaInstalacion,
  };
};
